import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { HttpErrorResponse } from '@angular/common/http';

import { ModuleConfiguration } from '../submission.module.config';

import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import { SubmittingEntityService } from './SubmittingEntityService';
import { SubmittingEntityListModel } from '../models/SubmittingEntityListModel';
import { AddSubmittingEntityModel } from '../models/AddSubmittingEntityModel';
import { QueryResponse } from '../../core/services/QueryResponse';
import { SubmittingEntityModel } from '../models/SubmittingEntityModel';
import { ModelResponse } from '../../core/services/ModelResponse';
import { ResponseMessage } from '../../core/services/ResponseMessage';
import { ResponseMessageType } from '../../core/services/ResponseMessageType';
import { LoadPagePayload } from '../../pillar/common/actions/LoadPagePayload';
import { PhysicalAddressModel } from '../../common/models/PhysicalAddressModel';
import { ContactModel } from '../models/ContactModel';
import { AddMappingModel } from '../models/AddMappingModel';
import { CONFIGURE_REPORTING, ConfigureReportingAction } from '../store/actions/ConfigureReportingAction';
import { ActionError } from '../../pillar/common/actions/ActionError';
import { ConfigureReportingSuccessAction } from '../store/actions/ConfigureReportingSuccessAction';
import { DeleteSubmittingEntityModel } from '../models/DeleteSubmittingEntityModel';
import { SubmittingEntityIdentifierModel } from '../models/SubmittingEntityIdentifierModel';

@Injectable()
export class SubmittingEntityServiceEffects {
  onlyOnce = true;
  @Effect()
  getSubmittingEntityList$: Observable<Action> = this.actions$.ofType(dashboardActions.getQueryListType<SubmittingEntityListModel>(ModuleConfiguration.moduleName, SubmittingEntityListModel))
    .map((a: dashboardActions.QueryListAction<SubmittingEntityListModel>) => a.payload)
    .switchMap((payload: LoadPagePayload) => this.submittingEntityService.getSubmittingEntityList(payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.searchTerm)
      .map((response: QueryResponse<SubmittingEntityListModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, SubmittingEntityListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, SubmittingEntityListModel, { items: response.items, totalCount: response.totalCount });
      }));

  @Effect()
  getSubmittingEntity$: Observable<Action> = this.actions$.ofType(dashboardActions.getSelectionType<SubmittingEntityModel>(ModuleConfiguration.moduleName, SubmittingEntityModel))
    .map((a: dashboardActions.GetSelectionAction<SubmittingEntityModel>) => a.payload)
    .switchMap((payload: { key: string }) => this.submittingEntityService.getSubmittingEntity(payload.key)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<SubmittingEntityModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<SubmittingEntityModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, SubmittingEntityListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.GetSelectionSuccessAction(ModuleConfiguration.moduleName, SubmittingEntityModel, { model: response.model });
      }));
  @Effect()
  addSubmittingEntity$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<AddSubmittingEntityModel>(ModuleConfiguration.moduleName, AddSubmittingEntityModel))
      .switchMap((a: dashboardActions.AddAction<AddSubmittingEntityModel>) => this.submittingEntityService.addSubmittingEntity(a.payload.model)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value) );
        }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, AddSubmittingEntityModel, { key: response.model });
      }));
  @Effect()
  addMapping$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<AddMappingModel>(ModuleConfiguration.moduleName, AddMappingModel))
      .switchMap((a: dashboardActions.AddAction<AddMappingModel>) => this.submittingEntityService.addMapping(a.payload.model.submittingEntityKey, a.payload.model)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, AddMappingModel, { key: response.model });
      }));
  @Effect()
  addAddress$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, PhysicalAddressModel>(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel))
    .filter((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, PhysicalAddressModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
    .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, PhysicalAddressModel>) => this.submittingEntityService.addAddress(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<PhysicalAddressModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<PhysicalAddressModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseAddress$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, PhysicalAddressModel>(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel))
    .filter((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, PhysicalAddressModel>) => a.payload.model.key !== undefined && a.payload.model.key !== '')    
    .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, PhysicalAddressModel>) => this.submittingEntityService.reviseAddress(a.payload.key, a.payload.model)
    
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<PhysicalAddressModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<PhysicalAddressModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          SubmittingEntityModel,
          PhysicalAddressModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  removeAddress$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<SubmittingEntityModel, PhysicalAddressModel>(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel))
    .switchMap((a: workspaceActions.RemoveModelAction<SubmittingEntityModel, PhysicalAddressModel>) => this.submittingEntityService.removeAddress(a.payload.key, a.payload.model.key)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<PhysicalAddressModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<PhysicalAddressModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName, SubmittingEntityModel, PhysicalAddressModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  addContact$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, ContactModel>(ModuleConfiguration.moduleName, SubmittingEntityModel, ContactModel))
    .filter((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, ContactModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
    .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, ContactModel>) => this.submittingEntityService.addContact(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<ContactModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<ContactModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName, SubmittingEntityModel, ContactModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseContact$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, ContactModel>(ModuleConfiguration.moduleName, SubmittingEntityModel, ContactModel))
    .filter((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, ContactModel>) => a.payload.model.key !== undefined && a.payload.model.key !== '')
    .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, ContactModel>) => this.submittingEntityService.reviseContact(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<ContactModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<ContactModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          SubmittingEntityModel,
          ContactModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  removeContact$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<SubmittingEntityModel, ContactModel>(ModuleConfiguration.moduleName, SubmittingEntityModel, ContactModel))
    .switchMap((a: workspaceActions.RemoveModelAction<SubmittingEntityModel, ContactModel>) =>
      this.submittingEntityService.removeContact(a.payload.key, a.payload.model.key)
        .catch((error: HttpErrorResponse) => {
          var response = new ModelResponse<ContactModel>();
          response.hasError = true;
          var responseMessage = new ResponseMessage();
          responseMessage.type = ResponseMessageType.Error;
          responseMessage.value = error.message;
          response.messages = [responseMessage];
          return Observable.of(response);
        })
        .map((response: ModelResponse<ContactModel>) => {
          if (response.hasError) {
            return new workspaceActions.UpdateModelErrorAction(
              {
                action: a,
                errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
              });
          }
          return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
            SubmittingEntityModel,
            ContactModel,
            {
              key: a.payload.key,
              model: response.model,
              control: a.payload.control
            });
        }));
  @Effect()
  reviseName$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, string>(ModuleConfiguration.moduleName, SubmittingEntityModel, 'name'))
    .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, string>) => this.submittingEntityService.reviseName(a.payload.key, a.payload.model)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          SubmittingEntityModel,
          'name',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  addIdentifier$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, SubmittingEntityIdentifierModel>(ModuleConfiguration.moduleName, SubmittingEntityModel, SubmittingEntityIdentifierModel))
    .filter((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, SubmittingEntityIdentifierModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
    .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, SubmittingEntityIdentifierModel>) => this.submittingEntityService.addIdentifier(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<SubmittingEntityIdentifierModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<SubmittingEntityIdentifierModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName, SubmittingEntityModel, SubmittingEntityIdentifierModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseIdentifier$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, SubmittingEntityIdentifierModel>(ModuleConfiguration.moduleName, SubmittingEntityModel, SubmittingEntityIdentifierModel))
    .filter((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, SubmittingEntityIdentifierModel>) => a.payload.model.key !== undefined && a.payload.model.key !== '')
    .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, SubmittingEntityIdentifierModel>) => this.submittingEntityService.reviseIdentifier(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<SubmittingEntityIdentifierModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<SubmittingEntityIdentifierModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          SubmittingEntityModel,
          SubmittingEntityIdentifierModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseAllowedDataSets$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, string[]>(ModuleConfiguration.moduleName, SubmittingEntityModel, 'allowedDataSets'))
      .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, string[]>) => this.submittingEntityService.reviseAllowedDataSets(a.payload.key, a.payload.model)
      .map((response: ModelResponse<string[]>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          SubmittingEntityModel,
          'allowedDataSets',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseAutoGenerateSftpXmlJobReports$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<SubmittingEntityModel, Boolean>(ModuleConfiguration.moduleName, SubmittingEntityModel, Boolean))
    .switchMap((a: workspaceActions.UpdateModelAction<SubmittingEntityModel, boolean>) => this.submittingEntityService.reviseAutoGenerateSftpXmlJobReports(a.payload.key, a.payload.model)
      .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          SubmittingEntityModel,
          'reviseAutoGenerateSftpXmlJobReports',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  configureReporting$: Observable<Action> = this.actions$.ofType(CONFIGURE_REPORTING)
    .switchMap((action: ConfigureReportingAction) => this.submittingEntityService.configureReporting(action.payload.submittingEntityKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<boolean>(error)))
      .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
        return new ConfigureReportingSuccessAction();
      }));
  @Effect()
  deleteSubmittingEntity$: Observable<Action> = this.actions$.ofType(dashboardActions.deleteType<DeleteSubmittingEntityModel>(ModuleConfiguration.moduleName, DeleteSubmittingEntityModel))
      .switchMap((a: dashboardActions.DeleteAction<DeleteSubmittingEntityModel>) => this.submittingEntityService.deleteSubmittingEntity(a.payload.model)
        .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
          return new dashboardActions.DeleteSuccessAction(ModuleConfiguration.moduleName, DeleteSubmittingEntityModel, { key: response.model });
      }));

  constructor(
    private submittingEntityService: SubmittingEntityService,
    private actions$: Actions
  ) { }
}



// WEBPACK FOOTER //
// ./src/app/submission/services/SubmittingEntityService.effects.ts